import { lazy, Suspense, useMemo } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  redirect,
  useParams,
  type LoaderFunctionArgs,
  type ShouldRevalidateFunctionArgs,
} from 'react-router-dom';
import type { Action } from '@/services/auth/capabilities';
import { requireAuth } from '@/services/auth/loaderGuards';
import { toast } from 'sonner';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { QueryProvider } from '@/providers/QueryProvider';
import { Toaster } from '@/components/ui/sonner';
import { GlobalConfirmDialog } from '@/components/Dialog/GlobalConfirmDialog';
import { GlobalDevPanel } from '@/components/DevPanel/GlobalDevPanel';
import DevErrorPreview, { devErrorPreviewLoader } from '@/components/DevPanel/DevErrorPreview';
import ProtectedRoute from '@/components/Layout/ProtectedRoute';
import RouteProgressBar from '@/components/Layout/RouteProgressBar';
import Layout from '@/components/Layout/Layout';
import ErrorBoundary from '@/components/Layout/ErrorBoundary';
import RouteErrorBoundary from '@/components/Layout/RouteErrorBoundary';
import PageLoadingFallback from '@/components/ui/PageLoadingFallback';
import { queryClient } from '@/api/queryClient';
import { ensureTransactionHydrated } from '@/services/transactions/hooks';
import {
  preloadTransactionForm,
  preloadUpload,
  preloadJobs,
  preloadAnalytics,
  preloadTeamMembers,
  preloadTeamSettings,
  preloadWebhooks,
  preloadModelRegistry,
  preloadApiDocs,
  preloadUserGuide,
} from '@/pages/preloadRoutes';
import { parseAnalyticsFilters } from '@/hooks/analytics/useAnalyticsFilters';
import {
  usageQueryOptions,
  curationQueryOptions,
  extractionOutputQueryOptions,
  substanceValidationQueryOptions,
  extractionHealthQueryOptions,
} from '@/services/analytics/hooks';
import type { AnalyticsFilters } from '@/api/types';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const UploadPage = lazy(preloadUpload);
const TransactionFormPage = lazy(preloadTransactionForm);
const JobsPage = lazy(preloadJobs);
const TeamSettingsPage = lazy(preloadTeamSettings);
const AnalyticsLayout = lazy(preloadAnalytics);
const UsageTab = lazy(() => import('@/components/Analytics/tabs/UsageTab'));
const CurationTab = lazy(() => import('@/components/Analytics/tabs/CurationTab'));
const ExtractionOutputTab = lazy(() => import('@/components/Analytics/tabs/ExtractionOutputTab'));
const ExtractionHealthTab = lazy(() => import('@/components/Analytics/tabs/ExtractionHealthTab'));
const ValidationTab = lazy(() => import('@/components/Analytics/tabs/ValidationTab'));
const TeamMembersPage = lazy(preloadTeamMembers);
const WebhooksPage = lazy(preloadWebhooks);
const ModelRegistryPage = lazy(preloadModelRegistry);
const ApiDocsPage = lazy(preloadApiDocs);
const UserGuidePage = lazy(preloadUserGuide);

// Layout hides its own chrome when embedded (see Layout.tsx), so the embed
// entry routes below reuse the standard protected page.
const protectedPage = (Page: React.LazyExoticComponent<React.ComponentType>, requires?: Action) => (
  <ProtectedRoute requires={requires}>
    <Layout>
      <Suspense fallback={<PageLoadingFallback />}>
        <Page />
      </Suspense>
    </Layout>
  </ProtectedRoute>
);

// Once for the whole router: a per-page mount unmounts mid-navigation, and routes outside
// Layout would show no bar at all.
const RootRoute: React.FC = () => (
  <>
    <RouteProgressBar />
    <Outlet />
  </>
);

const TransactionFormRoute: React.FC = () => {
  const { transactionId } = useParams();
  return (
    <ProtectedRoute>
      <Layout>
        <Suspense fallback={<PageLoadingFallback />}>
          <TransactionFormPage key={transactionId} />
        </Suspense>
      </Layout>
    </ProtectedRoute>
  );
};

// See CONTEXT.md > Transaction artifact cache for the loader/refetch error contract.
async function transactionLoader({ params, request }: LoaderFunctionArgs) {
  requireAuth(request);
  const transactionId = params.transactionId;
  if (!transactionId) return null;
  try {
    await ensureTransactionHydrated(transactionId);
  } catch (err) {
    console.error('[transactionLoader] Failed to load transaction:', err);
    toast.error('Failed to load transaction. Please upload a new document or try again.');
    throw redirect('/');
  }
  return null;
}

// Section, CTU and cargo ids are route params, so by default every tree click counts as a new
// route instance and re-runs the loader.
const revalidateOnTransactionChange = ({
  currentParams,
  nextParams,
}: ShouldRevalidateFunctionArgs) => currentParams.transactionId !== nextParams.transactionId;

const analyticsLoader =
  <O,>(buildOpts: (f: AnalyticsFilters) => O) =>
  async ({ request }: LoaderFunctionArgs) => {
    requireAuth(request);
    const filters = parseAnalyticsFilters(new URL(request.url).searchParams);
    try {
      await queryClient.ensureQueryData(
        buildOpts(filters) as Parameters<typeof queryClient.ensureQueryData>[0]
      );
    } catch {
      /* per-tab component handles error UI */
    }
    return null;
  };

export const routes = [
  {
    path: '/',
    element: <RootRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: 'login',
        element: (
          <Suspense fallback={<PageLoadingFallback />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        index: true,
        element: protectedPage(UploadPage),
      },
      {
        path: 'embed/:transactionId/:section?/:ctuId?/:cargoId?',
        element: protectedPage(TransactionFormPage),
        loader: transactionLoader,
        shouldRevalidate: revalidateOnTransactionChange,
      },
      {
        path: 'embed',
        element: protectedPage(UploadPage),
      },
      {
        path: 'embed/settings',
        element: protectedPage(TeamSettingsPage, 'team.settings.edit'),
      },
      {
        path: ':transactionId/:section?/:ctuId?/:cargoId?',
        element: <TransactionFormRoute />,
        loader: transactionLoader,
        shouldRevalidate: revalidateOnTransactionChange,
      },
      {
        path: 'jobs',
        element: protectedPage(JobsPage),
      },
      {
        path: 'batch-upload',
        element: <Navigate to="/" replace />,
      },
      {
        path: 'analytics',
        element: protectedPage(AnalyticsLayout, 'analytics.view'),
        children: [
          { index: true, element: <Navigate to="usage" replace /> },
          {
            path: 'usage',
            element: (
              <Suspense fallback={<PageLoadingFallback />}>
                <UsageTab />
              </Suspense>
            ),
            loader: analyticsLoader(usageQueryOptions),
          },
          {
            path: 'curation',
            element: (
              <Suspense fallback={<PageLoadingFallback />}>
                <CurationTab />
              </Suspense>
            ),
            loader: analyticsLoader(curationQueryOptions),
          },
          {
            path: 'output',
            element: (
              <Suspense fallback={<PageLoadingFallback />}>
                <ExtractionOutputTab />
              </Suspense>
            ),
            loader: analyticsLoader(extractionOutputQueryOptions),
          },
          {
            path: 'health',
            element: (
              <Suspense fallback={<PageLoadingFallback />}>
                <ExtractionHealthTab />
              </Suspense>
            ),
            loader: analyticsLoader(extractionHealthQueryOptions),
          },
          {
            path: 'validation',
            element: (
              <Suspense fallback={<PageLoadingFallback />}>
                <ValidationTab />
              </Suspense>
            ),
            loader: analyticsLoader(substanceValidationQueryOptions),
          },
        ],
      },
      {
        path: 'team-members',
        element: protectedPage(TeamMembersPage, 'team.members.view'),
      },
      {
        path: 'team-settings',
        element: protectedPage(TeamSettingsPage, 'team.settings.edit'),
      },
      {
        path: 'webhooks',
        element: protectedPage(WebhooksPage, 'webhooks.manage'),
      },
      {
        path: 'model-registry',
        element: protectedPage(ModelRegistryPage, 'admin.models.manage'),
      },
      {
        path: 'docs',
        element: protectedPage(ApiDocsPage),
      },
      {
        path: 'guide',
        element: protectedPage(UserGuidePage),
      },
      {
        path: 'dev/error-preview',
        loader: devErrorPreviewLoader,
        element: (
          <ProtectedRoute requires="admin.dev-panel">
            <DevErrorPreview />
          </ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
];

function App() {
  const router = useMemo(() => createBrowserRouter(routes), []);
  return (
    <QueryProvider>
      <ErrorBoundary>
        <ThemeProvider>
          <Toaster />
          <GlobalConfirmDialog />
          <GlobalDevPanel />
          <RouterProvider router={router} />
        </ThemeProvider>
      </ErrorBoundary>
    </QueryProvider>
  );
}

export default App;
